import { i18n } from '@/shared/i18n';
import type { ConnectorPlatform } from '@/entities/media-connector';
import { CREATIVE_FIELD_PREFIX } from '../model/platform-structure';
import type { ConnectorCapabilities, ConnectorHealth } from '../model/types';
import type {
  DeliveryJob,
  JobFieldValue,
  JobIssue,
  JobIssueSeverity,
  JobIssueSource,
  JobNode,
  JobPlan,
  JobPlatformGroup,
  JobDeliveryState,
  JobPublishOutcome,
  JobPublishReport,
  JobSourceKind,
  JobStatus,
  NodeAction,
  NodePublishOutcome,
} from '../model/job.types';

/** The slices of media-api's job DTOs this app reads. */
export interface ApiJobPlatform {
  platform: string;
  connectorId: string | null;
}

export interface ApiJob {
  id: string;
  projectId: string | null;
  title: string;
  status: string;
  sourceFileName: string;
  sourceFileKind: string;
  sourceRowCount: number;
  sourceTemplateName: string | null;
  sourceError?: string | null;
  isReadingSource?: boolean;
  adCount?: number | null;
  hasSource?: boolean;
  updatedAt?: string;
  platforms: ApiJobPlatform[];
  createdBy: string | null;
  publishedAt: string | null;
  createdAt: string;
  preflight: {
    lastRunAt: string;
    errors: number;
    warnings: number;
    advice: number;
    isStale: boolean;
  } | null;
  dryRun: {
    lastRunAt: string;
    checked: number;
    rejected: number;
    unchecked: number;
  } | null;
}

export interface ApiJobIssue {
  fieldKey: string;
  severity: string;
  message: string;
  sourceRow?: number;
  source?: string;
  /** Which rule produced it, where the UI has an action for a particular one. */
  ruleId?: string;
  guidelineId?: string;
  why?: string;
  howToFix?: string;
  referenceUrl?: string;
  ownerName?: string;
}

export interface ApiJobNode {
  id: string;
  level: string;
  name: string;
  action: string;
  externalId: string | null;
  sourceRows: number[];
  fields: Record<string, unknown>;
  editedFields: string[];
  issues: ApiJobIssue[];
  publishOutcome?: string;
  publishError?: string | null;
  deliveryState?: string | null;
  children: ApiJobNode[];
}

export interface ApiJobPlan {
  jobId: string;
  platforms: {
    platform: string;
    connectorId: string | null;
    pageId?: string | null;
    pageName?: string | null;
    advertiserId?: string | null;
    advertiserName?: string | null;
    needsAdvertiser?: boolean;
    connectorStatus?: string | null;
    connectorCapabilities?: ConnectorCapabilities | null;
    accountName?: string | null;
    dryRunSupported?: boolean;
    nodes: ApiJobNode[];
  }[];
}

export interface ApiJobList {
  items: ApiJob[];
  total: number;
}

export interface ApiJobPublishNodeResult {
  nodeId: string;
  platform: string;
  level: string;
  name: string;
  outcome: string;
  externalId: string | null;
  reason: string | null;
  deliveryState?: string | null;
  deliveryDetail?: string | null;
}

export interface ApiJobPublishReport {
  jobId: string;
  status: string;
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  failed: number;
  live?: number;
  inReview?: number;
  rejected?: number;
  lastDeliveryCheckAt?: string | null;
  results: ApiJobPublishNodeResult[];
}

/** media-api's `adGroup` is this app's `ad_group`; the fourth level folds onto the ad. */
const LEVEL_BY_API_LEVEL: Record<string, JobNode['level']> = {
  campaign: 'campaign',
  adGroup: 'ad_group',
  ad: 'ad',
};

export function toDeliveryJob(api: ApiJob): DeliveryJob {
  return {
    id: api.id,
    projectId: api.projectId ?? '',
    title: api.title,
    status: api.status as JobStatus,
    source: {
      fileName: api.sourceFileName,
      kind: api.sourceFileKind as JobSourceKind,
      rowCount: api.sourceRowCount,
      uploadedAt: api.createdAt,
      templateName: api.sourceTemplateName,
      // Absent on a job uploaded before the file was kept, which is why the list links
      // some rows and not others.
      isDownloadable: api.hasSource ?? false,
    },
    platforms: api.platforms.map((each) => each.platform as ConnectorPlatform),
    adCount: api.adCount ?? api.sourceRowCount,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt ?? api.createdAt,
    createdBy: api.createdBy ?? '',
    publishedAt: api.publishedAt,
    preflight: api.preflight ?? null,
    dryRun: api.dryRun ?? null,
    sourceError: api.sourceError ?? null,
    isReadingSource: api.isReadingSource ?? false,
  };
}

export function toJobPlan(api: ApiJobPlan): JobPlan {
  return {
    jobId: api.jobId,
    groups: api.platforms.map(
      (group): JobPlatformGroup => ({
        platform: group.platform as ConnectorPlatform,
        connectorId: group.connectorId,
        // Now answered by the plan rather than resolved from the connectors list: only
        // the server can say whether the bound account's credentials still work, and
        // that has to be on screen before anything is run.
        accountName: group.accountName ?? null,
        connectorStatus: (group.connectorStatus as ConnectorHealth | null) ?? null,
        connectorCapabilities: group.connectorCapabilities ?? null,
        dryRunSupported: group.dryRunSupported,
        pageId: group.pageId ?? null,
        pageName: group.pageName ?? null,
        advertiserId: group.advertiserId ?? null,
        advertiserName: group.advertiserName ?? null,
        needsAdvertiser: group.needsAdvertiser ?? false,
        nodes: group.nodes.map(toJobNode),
      }),
    ),
  };
}

/**
 * One API node becomes one row-level node, with any creative child folded into it.
 *
 * The plan tree has creative as a child of the ad because the platform does — an ad
 * references a creative that must exist first — but a media plan is one row per ad,
 * and so is the review table. The creative's values ride on the ad under a prefix,
 * carrying the child's id so an edit can be routed back to the right object.
 */
function toJobNode(api: ApiJobNode): JobNode {
  const creative = api.children.find((child) => child.level === 'creative');
  const children = api.children.filter((child) => child.level !== 'creative');

  const fields = [
    nameField(api.name),
    // The id is the row's identity rather than one of its values, so the API keeps it
    // off the field bag. The sheet still has a column for it — that is how a plan says
    // "change this one" — and an always-blank column would read as a broken one.
    ...(api.externalId ? [{ key: EXTERNAL_ID_FIELD, value: api.externalId }] : []),
    ...toFieldValues(api.fields, api.editedFields),
    ...(creative
      ? [
          nameField(creative.name, CREATIVE_FIELD_PREFIX),
          ...toFieldValues(creative.fields, creative.editedFields, CREATIVE_FIELD_PREFIX),
        ]
      : []),
  ];

  return {
    id: api.id,
    level: LEVEL_BY_API_LEVEL[api.level] ?? 'ad',
    action: api.action as NodeAction,
    externalId: api.externalId,
    sourceRows: api.sourceRows,
    fields,
    parseIssues: [
      ...api.issues.map(toJobIssue),
      ...(creative?.issues ?? []).map((issue) =>
        toJobIssue({ ...issue, fieldKey: `${CREATIVE_FIELD_PREFIX}${issue.fieldKey}` }),
      ),
      // A failed publish is raised as a finding rather than a separate channel, so it
      // lands wherever findings already render. The publish report only exists as the
      // response to the call that produced it; this is the same failure read back off the
      // row, which is all a reviewer returning to a partly published job has.
      ...publishFailureIssues(api),
      ...publishFailureIssues(creative, CREATIVE_FIELD_PREFIX),
    ],
    ...(api.publishOutcome ? { publishOutcome: api.publishOutcome as NodePublishOutcome } : {}),
    ...(api.publishError !== undefined ? { publishError: api.publishError } : {}),
    ...(api.deliveryState ? { deliveryState: api.deliveryState as JobDeliveryState } : {}),
    ...(creative ? { creativeNodeId: creative.id } : {}),
    children: children.map(toJobNode),
  };
}

/** Matches the canonical key media-api adds to every level's schema. */
const EXTERNAL_ID_FIELD = 'externalId';

function nameField(value: string, prefix = ''): JobFieldValue {
  return { key: `${prefix}name`, value };
}

/**
 * The API reports WHICH fields a reviewer changed, not what they held before, so
 * `edited` is carried explicitly rather than inferred by comparing the two values.
 */
function toFieldValues(
  fields: Record<string, unknown>,
  editedFields: string[],
  prefix = '',
): JobFieldValue[] {
  const edited = new Set(editedFields);
  return Object.entries(fields).map(([key, value]) => ({
    key: `${prefix}${key}`,
    value: toDisplayValue(value),
    ...(edited.has(key) ? { edited: true } : {}),
  }));
}

/** Lists arrive as arrays; the table and its editors work in text. */
function toDisplayValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return null;
  return String(value);
}

/**
 * The report the publisher returned.
 *
 * `pending` is dropped rather than mapped: it is the pre-publish default on a node,
 * so a node still carrying it was never reached, and listing it as a result would
 * pad the report with rows that describe nothing.
 */
export function toPublishReport(api: ApiJobPublishReport): JobPublishReport {
  return {
    status: api.status as JobStatus,
    created: api.created,
    updated: api.updated,
    unchanged: api.unchanged,
    skipped: api.skipped,
    failed: api.failed,
    live: api.live ?? 0,
    inReview: api.inReview ?? 0,
    rejected: api.rejected ?? 0,
    ...(api.lastDeliveryCheckAt ? { lastDeliveryCheckAt: api.lastDeliveryCheckAt } : {}),
    results: api.results
      .filter((result) => result.outcome !== 'pending')
      .map((result) => ({
        nodeId: result.nodeId,
        name: result.name,
        level:
          result.level === 'creative' ? 'creative' : (LEVEL_BY_API_LEVEL[result.level] ?? 'ad'),
        platform: result.platform as ConnectorPlatform,
        outcome: result.outcome as JobPublishOutcome,
        ...(result.reason ? { reason: result.reason } : {}),
        ...(result.deliveryState
          ? { deliveryState: result.deliveryState as JobDeliveryState }
          : {}),
        ...(result.deliveryDetail ? { deliveryDetail: result.deliveryDetail } : {}),
      })),
  };
}

/**
 * Anything unrecognised becomes an error rather than being dropped: a finding we
 * cannot classify is still a finding, and failing loud beats a plan that looks
 * clean because the server sent a severity this build predates.
 */
function toSeverity(value: string): JobIssueSeverity {
  if (value === 'warning') return 'warning';
  if (value === 'advice') return 'advice';
  return 'error';
}

/**
 * The stored publish failure as a finding, or nothing at all.
 *
 * Only `failed` produces one. `skipped` is a row that never got its turn because an
 * ancestor failed, and reporting it alongside the ancestor's real error would bury the one
 * message that says what went wrong.
 */
function publishFailureIssues(
  api: Pick<ApiJobNode, 'publishOutcome' | 'publishError'> | undefined,
  fieldPrefix = '',
): JobIssue[] {
  if (api?.publishOutcome !== 'failed') return [];
  return [
    {
      severity: 'error',
      field: fieldPrefix ? `${fieldPrefix}${PUBLISH_FIELD}` : null,
      message: api.publishError ?? i18n.t('projects:mediaCampaign.publishFailedWithoutReason'),
      source: 'platform',
      fromLastPublish: true,
    },
  ];
}

/** Names the failure against the row rather than a column, which is where it happened. */
// eslint-disable-next-line i18next/no-literal-string -- field key the UI matches on, not copy
const PUBLISH_FIELD = 'publish';

function toJobIssue(api: ApiJobIssue): JobIssue {
  return {
    severity: toSeverity(api.severity),
    field: api.fieldKey || null,
    message: api.sourceRow ? `${api.message} (row ${api.sourceRow})` : api.message,
    ...(api.source ? { source: api.source as JobIssueSource } : {}),
    ...(api.ruleId ? { ruleId: api.ruleId } : {}),
    ...(api.guidelineId ? { guidelineId: api.guidelineId } : {}),
    ...(api.why ? { why: api.why } : {}),
    ...(api.howToFix ? { howToFix: api.howToFix } : {}),
    ...(api.referenceUrl ? { referenceUrl: api.referenceUrl } : {}),
    ...(api.ownerName ? { ownerName: api.ownerName } : {}),
  };
}
